import {
  Component,
  EventEmitter,
  Input,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { Logger } from '../../../core/services/logger.service';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Dropdown } from 'primeng/dropdown';

interface DropdownOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
})
export class DropdownComponent implements ControlValueAccessor {
  private readonly logger = new Logger(DropdownComponent.name);

  @Input() label?: string;
  @Input() disabled = false;
  @Input() placeholder = '';
  @Input() description?: string;
  @Input() errorMessages: string[] = [];
  @Input() options: DropdownOption[] = [];
  @Input() filter = false;

  @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() filterChange: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('pDropdown', { static: false }) pDropdown!: Dropdown;

  protected _value = '';

  constructor(@Self() @Optional() public control: NgControl) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: string): void {
    this._value = value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  get value(): string {
    return this._value;
  }

  set value(value: string) {
    if (value !== this._value) {
      this._value = value;
      this.onChange(value);
    }
    this.onTouched();
    this.valueChange.emit(value);
  }

  public getValidationNgClasses(
    control: NgControl
  ): Record<string, boolean | null> {
    return {
      'ng-invalid': control?.invalid,
      'ng-valid': control?.valid,
      'ng-touched': control?.touched,
      'ng-untouched': !control?.touched,
      'ng-dirty': control?.dirty,
      'ng-pristine': control?.pristine,
    };
  }

  onDropdownShow(): void {
    // Vérifier si nous sommes sur mobile (largeur d'écran < 768px)
    if (window.innerWidth < 768) {
      setTimeout(() => {
        try {
          // Rechercher tous les panels de dropdown ouverts
          const openPanels = document.querySelectorAll(
            '.p-dropdown-panel.p-component.p-ripple-disabled'
          );

          // Trouver le panel qui contient un filtre
          let filterInput: Element | null = null;

          for (const panel of Array.from(openPanels)) {
            const input = panel.querySelector('.p-dropdown-filter');
            if (input) {
              filterInput = input;
              break;
            }
          }

          // Si aucun filtre n'est trouvé, essayer une recherche globale
          if (!filterInput) {
            filterInput = document.querySelector(
              '.p-dropdown-panel.p-component .p-dropdown-filter'
            );
          }

          if (filterInput) {
            // Mettre le focus sur l'input de filtre
            (filterInput as HTMLElement).focus();

            // Centrer le dropdown dans le viewport sans utiliser scrollIntoView
            setTimeout(() => {
              const rect = filterInput.getBoundingClientRect();
              const scrollTop =
                window.scrollY || document.documentElement.scrollTop;
              const targetY =
                rect.top +
                100 +
                scrollTop -
                window.innerHeight / 2 +
                rect.height / 2;
              window.scrollTo({ top: targetY, behavior: 'auto' });
            }, 300);
          }
        } catch (error) {
          this.logger.error('Erreur lors de la mise au point du dropdown', error);
        }
      }, 200); // Délai plus long pour s'assurer que le dropdown est complètement ouvert
    }
  }

  public openDropdown(): void {
    if (this.pDropdown) {
      this.pDropdown.show();
    }
  }
}
